import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Send, Banknote, X, AlertTriangle , Pencil} from 'lucide-react';
import { apiClient , extractErrorMessage} from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-D5 — receivables & chase. Who owes what, what's sent, what's overdue,
// oldest first. The operator marks invoices sent and records payments. The
// system prepares and tracks; it never sends anything or decides money.

interface Receivable {
  client_id: string; name_en: string; open_invoices: number;
  outstanding_sar: string; overdue_sar: string; oldest_due: string | null;
}
interface Invoice {
  id: string; code: string; status: string; issued_date: string; due_date: string | null; paid_at: string | null;
  client_name: string | null; total_sar: string; total_tons: string; trip_count: number; paid_sar: string; past_due: boolean;
}

const sar = (v: string | number | null) => (v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

export default function DumperReceivablesPage() {
  const qc = useQueryClient();
  // The invoice whose loads are open, and the removal in flight.
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [rmReason, setRmReason] = useState('');
  const [rmBusy, setRmBusy] = useState<string | null>(null);
  const [rmNote, setRmNote] = useState<string | null>(null);

  const { data: invoiceLoads, refetch: refetchLoads } = useQuery({
    queryKey: ['dumper-invoice-loads', editInvoice?.id],
    enabled: !!editInvoice,
    queryFn: async () => (await apiClient.get<{ items: Array<{
      id: string; code: string; tripDate: string; tons: string | null;
      revenue: string | null; plate: string | null; driver: string | null;
    }> }>(`/api/v1/dumper-invoices/${editInvoice!.id}/loads`)).data.items,
  });

  const removeLoad = async (loadId: string) => {
    if (!editInvoice) return;
    setRmBusy(loadId); setRmNote(null);
    try {
      const { data } = await apiClient.post<{ message: string; warning: string | null }>(
        `/api/v1/dumper-invoices/${editInvoice.id}/remove-load`,
        { loadId, reason: rmReason.trim() },
      );
      setRmNote([data.message, data.warning].filter(Boolean).join(' '));
      await refetchLoads();
      void qc.invalidateQueries({ queryKey: ['dumper-invoices'] });
      void qc.invalidateQueries({ queryKey: ['dumper-receivables'] });
    } catch (e) {
      setRmNote(extractErrorMessage(e));
    } finally {
      setRmBusy(null);
    }
  };

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['dumper-receivables'] }); qc.invalidateQueries({ queryKey: ['dumper-invoices'] }); };

  const { data: receivables } = useQuery({
    queryKey: ['dumper-receivables'],
    queryFn: async () => (await apiClient.get<{ items: Receivable[] }>('/api/v1/dumper-invoices/receivables')).data.items,
  });
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['dumper-invoices'],
    queryFn: async () => (await apiClient.get<{ items: Invoice[] }>('/api/v1/dumper-invoices')).data.items,
  });

  const sendMut = useMutation({ mutationFn: (id: string) => apiClient.post(`/api/v1/dumper-invoices/${id}/send`), onSuccess: invalidate });
  const cancelMut = useMutation({ mutationFn: (id: string) => apiClient.post(`/api/v1/dumper-invoices/${id}/cancel`), onSuccess: invalidate });

  const [payId, setPayId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const payMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/v1/dumper-invoices/${id}/pay`, { amountSar: Number(amount || 0), paymentMethod: method }),
    onSuccess: () => { setPayId(null); setAmount(''); invalidate(); },
  });

  const rec = receivables ?? [];
  const inv = invoices ?? [];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Dumpers · Receivables & chase</div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/dumpers/invoice-new"><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Build invoice</Button></Link>
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dumpers home
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8 space-y-8">
        {/* Chase list — who owes what, overdue first */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Who owes what</h3>
          {rec.length === 0 ? (
            <div className="text-sm text-muted-foreground rounded-xl border border-border bg-card p-4">Nothing outstanding.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rec.map((r) => (
                <div key={r.client_id} className="rounded-xl border border-border bg-card p-4">
                  <div className="font-medium">{r.name_en}</div>
                  <div className="text-2xl font-semibold mt-1">{sar(r.outstanding_sar)} <span className="text-xs font-normal text-muted-foreground">SAR outstanding</span></div>
                  <div className="text-sm mt-1 flex items-center gap-2">
                    {Number(r.overdue_sar) > 0 && (
                      <span className="inline-flex items-center gap-1 text-rose-600"><AlertTriangle className="h-3.5 w-3.5" /> {sar(r.overdue_sar)} overdue</span>
                    )}
                    <span className="text-muted-foreground">{r.open_invoices} open</span>
                  </div>
                  {r.oldest_due && <div className="text-xs text-muted-foreground mt-1">oldest due {r.oldest_due.slice(0, 10)}</div>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Invoices */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Invoices</h3>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-start font-medium px-3 py-2">Invoice</th>
                  <th className="text-start font-medium px-3 py-2">Client</th>
                  <th className="text-start font-medium px-3 py-2">Status</th>
                  <th className="text-end font-medium px-3 py-2">Total</th>
                  <th className="text-end font-medium px-3 py-2">Paid</th>
                  <th className="text-end font-medium px-3 py-2">Balance</th>
                  <th className="text-start font-medium px-3 py-2">Due</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
                {!isLoading && inv.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">No invoices yet. <Link className="underline" to="/dashboard/dumpers/invoice-new">Build one</Link>.</td></tr>
                )}
                {inv.map((i) => {
                  const balance = Number(i.total_sar) - Number(i.paid_sar);
                  return (
                    <tr key={i.id} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{i.code}</div>
                        <div className="text-xs text-muted-foreground">{i.trip_count} loads · {Number(i.total_tons).toLocaleString(undefined, { maximumFractionDigits: 1 })} t</div>
                      </td>
                      <td className="px-3 py-2">{i.client_name ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs rounded-full px-2 py-0.5 border ${
                          i.status === 'paid' ? 'border-emerald-500/40 text-emerald-600'
                          : i.past_due ? 'border-rose-500/40 text-rose-600'
                          : i.status === 'sent' ? 'border-blue-500/40 text-blue-600'
                          : i.status === 'cancelled' ? 'border-border text-muted-foreground'
                          : 'border-amber-500/40 text-amber-600'}`}>
                          {i.past_due ? 'overdue' : i.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-end">{sar(i.total_sar)}</td>
                      <td className="px-3 py-2 text-end text-muted-foreground">{sar(i.paid_sar)}</td>
                      <td className="px-3 py-2 text-end font-medium">{sar(balance)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{i.due_date ? i.due_date.slice(0, 10) : '—'}</td>
                      <td className="px-3 py-2 text-end whitespace-nowrap">
                        {/* Not offered on a paid invoice: the server refuses it,
                            and a button that cannot work is a worse lie than
                            no button. */}
                        {i.status !== 'paid' && i.status !== 'cancelled' && (
                          <Button size="sm" variant="ghost" className="gap-1 me-1"
                                  onClick={() => { setEditInvoice(i); setRmReason(''); setRmNote(null); }}>
                            <Pencil className="h-3.5 w-3.5" /> Edit loads
                          </Button>
                        )}
                        {i.status === 'draft' && (
                          <span className="inline-flex gap-1">
                            <Button size="sm" variant="outline" className="gap-1" disabled={sendMut.isPending} onClick={() => sendMut.mutate(i.id)}><Send className="h-3.5 w-3.5" /> Mark sent</Button>
                            <Button size="sm" variant="ghost" onClick={() => cancelMut.mutate(i.id)}><X className="h-3.5 w-3.5" /></Button>
                          </span>
                        )}
                        {(i.status === 'sent') && (
                          payId === i.id ? (
                            <span className="inline-flex items-center gap-1">
                              <input className="h-8 w-24 rounded-md border border-input bg-background px-2 text-sm text-end" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="amount" autoFocus />
                              <select className="h-8 rounded-md border border-input bg-background px-1 text-xs" value={method} onChange={(e) => setMethod(e.target.value)}>
                                <option value="bank_transfer">bank</option><option value="cash">cash</option><option value="cheque">cheque</option><option value="other">other</option>
                              </select>
                              <Button size="sm" disabled={!amount || payMut.isPending} onClick={() => payMut.mutate(i.id)}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => setPayId(null)}><X className="h-3.5 w-3.5" /></Button>
                            </span>
                          ) : (
                            <span className="inline-flex gap-1">
                              <Button size="sm" variant="outline" className="gap-1" onClick={() => { setPayId(i.id); setAmount(String(balance > 0 ? balance : '')); }}><Banknote className="h-3.5 w-3.5" /> Record payment</Button>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        {editInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
               onClick={() => setEditInvoice(null)}>
            <div className="bg-card border border-border rounded-lg p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg mb-1">{editInvoice.code} · loads</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Removing a load takes it off this invoice and corrects the total.
                It can then be deleted on the loads page.
              </p>

              <textarea
                value={rmReason}
                onChange={(e) => setRmReason(e.target.value)}
                rows={2}
                placeholder="Why is it being removed? (needed before you can remove)"
                className="w-full rounded-md border border-input bg-background p-2 text-sm mb-3"
              />

              {rmNote && (
                <div className="mb-3 rounded-md border border-border bg-muted/40 p-2 text-sm">{rmNote}</div>
              )}

              <table className="w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-start px-2 py-1 font-medium">Load</th>
                    <th className="text-start px-2 py-1 font-medium">Date</th>
                    <th className="text-start px-2 py-1 font-medium">Truck</th>
                    <th className="text-end px-2 py-1 font-medium">Tons</th>
                    <th className="text-end px-2 py-1 font-medium">SAR</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {(invoiceLoads ?? []).length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                      No loads on this invoice.
                    </td></tr>
                  )}
                  {(invoiceLoads ?? []).map((l) => (
                    <tr key={l.id} className="border-t border-border">
                      <td className="px-2 py-1 font-mono text-xs">{l.code}</td>
                      <td className="px-2 py-1">{l.tripDate?.slice(0, 10) ?? '—'}</td>
                      <td className="px-2 py-1">{l.plate ?? '—'}</td>
                      <td className="px-2 py-1 text-end">{l.tons ?? '—'}</td>
                      <td className="px-2 py-1 text-end">{l.revenue ? Number(l.revenue).toLocaleString() : '—'}</td>
                      <td className="px-2 py-1 text-end">
                        <Button
                          size="sm" variant="ghost"
                          className="text-rose-600 dark:text-rose-400"
                          disabled={rmBusy === l.id || rmReason.trim().length < 3}
                          onClick={() => void removeLoad(l.id)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4">
                <Button variant="outline" onClick={() => setEditInvoice(null)}>Close</Button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
